import { useEffect, useState, useCallback, useLayoutEffect, useMemo, Fragment, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Info, BarChart2, Table2, Package } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  supplyService,
  SupplyTableProduct,
  ProductDetailResponse,
  ProductDetailMonthlySales,
  ProductDetailStockLot,
  BomTraceExternalManagedBulkResult,
  ProductBomExplosionNode,
  ProductBomExplosionPayload,
  BomSupplyMetrics,
  BomParentLink,
} from '../../services/supplyService';
import { inventoryService, InventoryPolicy, UpdateReorderPointRequest } from '../../services/inventoryService';
import type { ApiError } from '../../services/api';
import { ReceivePurchaseOrderDialog } from './ReceivePurchaseOrderDialog';
import { useWarehouseId } from '../../contexts/WarehouseContext';
import { useCurrency } from '@/hooks/useCurrency';
import { productionScopeQueryParams } from '@/utils/productionScope';
import { TablePaginationBar } from '@/components/shared/TablePaginationBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { isBomTraceRowUrgent } from '../../utils/supplyUrgency';

interface ProductDetailSidebarProps {
  product: SupplyTableProduct | null;
  isOpen: boolean;
  onClose: () => void;
  onPolicyUpdated?: (productId: number) => Promise<void> | void;
  onBomTraceExternalManagedUpdated?: (productId: number, result?: BomTraceExternalManagedBulkResult) => Promise<void> | void;
  /** Tras marcar OC en tránsito como recibida (TASK 20). */
  onInTransitReceived?: (productId: number) => Promise<void> | void;
  productionWeek?: string;
  soExternalId?: string;
}

/**
 * Sección «Punto de reorden y stock de seguridad» del sidebar (Supply + Inventory Policies).
 * Oculta por decisión de producto; el código de edición/carga se mantiene por si se reactiva.
 */
const SHOW_REORDER_POINT_AND_SAFETY_STOCK_SECTION = false;

/** Meses del año en curso (enero → mes actual) para comparativa interanual en gráfica/tabla. */
function getCurrentYearMonthlyComparison(
  monthlySales: ProductDetailMonthlySales[] | undefined,
): ProductDetailMonthlySales[] {
  if (!monthlySales?.length) return [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  return monthlySales.filter((item) => {
    const [year, month] = item.month_key.split('-').map(Number);
    return year === currentYear && month >= 1 && month <= currentMonth;
  });
}

/** Formatea month_key (YYYY-MM) como "Mes Año" (ej. "Febrero 2025") */
function formatMonthAndYear(monthKey: string, locale: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, 1);
  const monthName = date.toLocaleString(locale, { month: 'long' });
  const capitalized = monthName.charAt(0).toUpperCase() + monthName.slice(1);
  return `${capitalized} ${y}`;
}

function formatExplosionQty(n: number): string {
  if (Number.isInteger(n)) return String(n);
  const s = n.toFixed(4).replace(/\.?0+$/, '');
  return s.length ? s : '0';
}

/** Total en tránsito desde detalle de compras (prioridad sobre fila de tabla). */
function inTransitQtyFromDetail(salesData: ProductDetailResponse | null): number | null {
  if (!salesData || salesData.in_transit_orders === undefined) return null;
  return salesData.in_transit_orders.reduce(
    (sum, order) =>
      sum +
      Number(
        order.pending_quantity ??
          Math.max(0, Number(order.quantity ?? 0) - Number(order.received_quantity ?? 0)),
      ),
    0,
  );
}

function isoWeekLabel(dateLike: string): string {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return '—';
  const utcDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `S${String(weekNo).padStart(2, '0')}`;
}

function bomExplosionTypeShort(t: TFunction, productType: string | null | undefined): string {
  if (!productType) return '—';
  const key = `supply.productDetail.bomExplosionType_${productType}`;
  const translated = t(key);
  return translated !== key ? translated : productType;
}

function UpstreamLinkMetricsRow({
  link,
  metrics,
  t,
}: {
  link: BomParentLink;
  metrics?: BomSupplyMetrics | null;
  t: TFunction;
}) {
  const bridge = t('supply.productDetail.bomExplosionQtyBridge', {
    qty: formatExplosionQty(link.qty_child_per_parent_unit),
    unit: link.unit_abbreviation,
    child: link.child_code,
    parent: link.parent_code,
  });
  const leadOk = metrics && metrics.lead_time_days !== null && metrics.lead_time_days !== undefined;
  return (
    <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-gray-100 pt-2 text-[11px] text-gray-600">
      <span className="max-w-[min(100%,22rem)] shrink-0 font-semibold leading-snug text-gray-800">{bridge}</span>
      {metrics ? (
        <>
          <span className="hidden text-gray-300 sm:inline" aria-hidden>
            |
          </span>
          <span>
            {t('supply.productDetail.bomExplosionStockActual')}:{' '}
            <strong className="text-gray-800">{formatExplosionQty(metrics.stock_actual)}</strong>
          </span>
          <span>
            {t('supply.productDetail.bomExplosionStockTransit')}:{' '}
            <strong className="text-gray-800">{formatExplosionQty(metrics.stock_in_transit)}</strong>
          </span>
          <span>
            {t('supply.productDetail.bomExplosionLeadTime')}:{' '}
            <strong className="text-gray-800">
              {leadOk
                ? `${metrics.lead_time_days} ${t('supply.productDetail.bomExplosionDaysShort')}`
                : '—'}
            </strong>
            {metrics.primary_supplier_name ? (
              <span className="font-normal text-gray-500"> ({metrics.primary_supplier_name})</span>
            ) : null}
          </span>
        </>
      ) : null}
    </div>
  );
}

function formatLotDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

function StockLotsSection({
  lots,
  t,
  locale,
}: {
  lots: ProductDetailStockLot[];
  t: TFunction;
  locale: string;
}) {
  if (!lots.length) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Package className="h-5 w-5 text-amber-600" aria-hidden />
        <h3 className="text-lg font-semibold text-gray-900">{t('supply.productDetail.stockLotsTitle')}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">{t('supply.productDetail.stockLotsDescription')}</p>
      <div className="overflow-x-auto -mx-1">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="px-2 py-2 font-medium">{t('supply.productDetail.stockLotsLotNumber')}</th>
              <th className="px-2 py-2 font-medium">{t('supply.productDetail.stockLotsSupplier')}</th>
              <th className="px-2 py-2 font-medium text-right">
                {t('supply.productDetail.stockLotsAvailable')}
                <span className="font-normal text-gray-400 ml-1">
                  ({t('supply.productDetail.stockLotsUnitAbbr')})
                </span>
              </th>
              <th className="px-2 py-2 font-medium">{t('supply.productDetail.stockLotsQuality')}</th>
              <th className="px-2 py-2 font-medium">{t('supply.productDetail.stockLotsExpiration')}</th>
              <th className="px-2 py-2 font-medium">{t('supply.productDetail.stockLotsStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {lots.map((lot) => {
              const rowClass = lot.is_expired
                ? 'bg-red-50'
                : lot.is_expiring_soon
                  ? 'bg-amber-50'
                  : '';
              const qualityKey = lot.quality_status
                ? `supply.productDetail.stockLotsQuality_${lot.quality_status}`
                : '';
              const statusKey = `supply.productDetail.stockLotsStatus_${lot.status}`;
              return (
                <tr key={lot.id} className={`border-b border-gray-100 ${rowClass}`}>
                  <td className="px-2 py-2 font-mono text-gray-900">
                    {lot.lot_number}
                    {lot.batch_code ? (
                      <span className="block text-gray-500 font-sans">{lot.batch_code}</span>
                    ) : null}
                  </td>
                  <td className="px-2 py-2 text-gray-700 max-w-[140px] truncate" title={lot.supplier_name ?? undefined}>
                    {lot.supplier_name?.trim() || '—'}
                  </td>
                  <td className="px-2 py-2 text-right text-gray-900 whitespace-nowrap">
                    <span className="font-medium tabular-nums">{lot.available_quantity}</span>
                    <span
                      className="ml-1 text-gray-500 font-normal"
                      title={lot.unit_name || lot.unit_abbreviation}
                    >
                      {lot.unit_abbreviation}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-gray-700">
                    {qualityKey ? t(qualityKey, { defaultValue: lot.quality_status ?? '—' }) : '—'}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span
                      className={
                        lot.is_expired
                          ? 'text-red-700 font-medium'
                          : lot.is_expiring_soon
                            ? 'text-amber-700 font-medium'
                            : 'text-gray-700'
                      }
                    >
                      {formatLotDate(lot.expiration_date, locale)}
                    </span>
                    {lot.is_expiring_soon && !lot.is_expired && (
                      <span className="ml-1 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
                        {t('supply.productDetail.stockLotsExpiringSoon')}
                      </span>
                    )}
                    {lot.is_expired && (
                      <span className="ml-1 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800">
                        {t('supply.productDetail.stockLotsExpired')}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-gray-700">
                    {t(statusKey, { defaultValue: lot.status })}
                    {lot.is_blocked ? (
                      <span className="block text-red-600">{t('supply.productDetail.stockLotsBlocked')}</span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BomSupplyMetricsBlock({ metrics, t }: { metrics?: BomSupplyMetrics | null; t: TFunction }) {
  if (!metrics) return null;
  const leadOk = metrics.lead_time_days !== null && metrics.lead_time_days !== undefined;
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 border-t border-gray-100 pt-2 text-[11px] text-gray-600">
      <span>
        {t('supply.productDetail.bomExplosionStockActual')}:{' '}
        <strong className="text-gray-800">{formatExplosionQty(metrics.stock_actual)}</strong>
      </span>
      <span>
        {t('supply.productDetail.bomExplosionStockTransit')}:{' '}
        <strong className="text-gray-800">{formatExplosionQty(metrics.stock_in_transit)}</strong>
      </span>
      <span>
        {t('supply.productDetail.bomExplosionLeadTime')}:{' '}
        <strong className="text-gray-800">
          {leadOk
            ? `${metrics.lead_time_days} ${t('supply.productDetail.bomExplosionDaysShort')}`
            : '—'}
        </strong>
        {metrics.primary_supplier_name ? (
          <span className="font-normal text-gray-500"> ({metrics.primary_supplier_name})</span>
        ) : null}
      </span>
    </div>
  );
}

function BomExplosionNodeView({
  node,
  t,
  variant = 'tree',
  selectedProductId,
  onSelectProduct,
}: {
  node: ProductBomExplosionNode;
  t: TFunction;
  variant?: 'tree' | 'focus';
  selectedProductId?: number;
  onSelectProduct?: (productId: number) => void;
}) {
  const levelLabel =
    variant === 'focus'
      ? t('supply.productDetail.bomExplosionFocusBadge')
      : node.level === 0
        ? t('supply.productDetail.bomExplosionLevelRoot')
        : t('supply.productDetail.bomExplosionLevelN', { n: node.level });

  const indentClass =
    variant === 'focus' ? '' : node.level > 0 ? 'ml-1 border-l-2 border-[#1681ba]/25 pl-3 pt-1' : '';

  const cardClass =
    variant === 'focus'
      ? 'flex flex-col gap-1 rounded-lg border-2 border-[#1681ba]/40 bg-sky-50/40 px-3 py-2 shadow-sm'
      : 'flex flex-col gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm';
  const isSelectable =
    variant === 'tree' &&
    typeof onSelectProduct === 'function' &&
    (selectedProductId == null || node.product_id !== selectedProductId);

  const interactiveClass = isSelectable
    ? 'cursor-pointer transition-colors hover:border-[#1681ba]/60 hover:bg-sky-50/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1681ba]/50'
    : '';

  const handleSelectNode = () => {
    if (!isSelectable || !onSelectProduct) return;
    onSelectProduct(node.product_id);
  };

  return (
    <div className={indentClass}>
      <div
        className={`${cardClass} ${interactiveClass}`}
        role={isSelectable ? 'button' : undefined}
        tabIndex={isSelectable ? 0 : undefined}
        onClick={isSelectable ? handleSelectNode : undefined}
        onKeyDown={
          isSelectable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectNode();
                }
              }
            : undefined
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-[#1681ba]">{levelLabel}</span>
          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700">
            {bomExplosionTypeShort(t, node.product_type)}
          </span>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
          <span className="font-mono text-sm font-semibold text-gray-900">{node.code}</span>
          <span className="text-sm text-gray-600">{node.name}</span>
        </div>
        {node.qty_in_parent != null && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
            <span>
              {t('supply.productDetail.bomExplosionQtyInParent')}:{' '}
              <strong className="text-gray-900">{formatExplosionQty(node.qty_in_parent)}</strong>{' '}
              {node.unit_abbreviation}
            </span>
          </div>
        )}
        <BomSupplyMetricsBlock metrics={node.supply_metrics} t={t} />
        {node.is_cycle_reference && (
          <p className="text-xs font-medium text-red-600">{t('supply.productDetail.bomExplosionCycle')}</p>
        )}
        {node.max_depth_reached && (
          <p className="text-xs font-medium text-amber-700">{t('supply.productDetail.bomExplosionMaxDepth')}</p>
        )}
      </div>
      {variant === 'tree' && node.children.length > 0 && (
        <div className="mt-2 space-y-2">
          {node.children.map((child, idx) => (
            <BomExplosionNodeView
              key={`${child.product_id}-L${child.level}-i${idx}`}
              node={child}
              t={t}
              selectedProductId={selectedProductId}
              onSelectProduct={onSelectProduct}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const UPSTREAM_DEPTH_BORDER = [
  'border-l-[#1681ba]',
  'border-l-indigo-500',
  'border-l-violet-500',
  'border-l-teal-600',
] as const;

function parentChainMatchesSearch(chain: BomParentLink[], query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = chain
    .flatMap((l) => [l.parent_code, l.parent_name, l.child_code, l.child_name])
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function BomExplosionParentChainsList({
  parent_chains,
  t,
  selectedProductId,
  onSelectProduct,
}: {
  parent_chains: BomParentLink[][];
  t: TFunction;
  selectedProductId?: number;
  onSelectProduct?: (productId: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);

  const filteredChains = useMemo(
    () => parent_chains.filter((ch) => parentChainMatchesSearch(ch, search)),
    [parent_chains, search]
  );

  useEffect(() => {
    setPage(1);
  }, [search, parent_chains, pageSize]);

  const total = filteredChains.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const pageChains = filteredChains.slice(startIdx, startIdx + pageSize);

  return (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-gray-800 mb-2">
        {t('supply.productDetail.bomExplosionParentChainsTitle')}
      </h4>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('supply.productDetail.bomExplosionChainSearchPlaceholder')}
          className="w-full sm:max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#1681ba] focus:outline-none focus:ring-1 focus:ring-[#1681ba]"
          aria-label={t('supply.productDetail.bomExplosionChainSearchPlaceholder')}
        />
        {total > 0 ? (
          <TablePaginationBar
            currentPage={safePage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        ) : null}
      </div>

      {total === 0 && parent_chains.length > 0 && (
        <p className="text-sm text-gray-500 mb-3">{t('supply.productDetail.bomExplosionChainNoResults')}</p>
      )}

      <div className="space-y-4">
        {pageChains.map((chain, ci) => {
          const routeIndex = startIdx + ci + 1;
          const chainKey = chain.map((l) => `${l.parent_product_id}-${l.child_product_id}`).join('|');
          return (
            <div
              key={chainKey}
              className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 p-4"
            >
              <p className="text-xs font-medium text-gray-500 mb-3">
                {t('supply.productDetail.bomExplosionChainRoute', { n: routeIndex })}
              </p>
              {[...chain].reverse().map((link, li) => {
                const depth = li + 1;
                const borderAccent = UPSTREAM_DEPTH_BORDER[li % UPSTREAM_DEPTH_BORDER.length];
                const isSelectable =
                  typeof onSelectProduct === 'function' &&
                  (selectedProductId == null || link.parent_product_id !== selectedProductId);
                return (
                  <div
                    key={`${link.parent_product_id}-${link.child_product_id}-${li}`}
                    className={`mb-2 border-l-4 pl-3 ${borderAccent}`}
                    style={{ marginLeft: Math.min(li * 14, 56) }}
                  >
                    <div
                      className={`rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm ${
                        isSelectable
                          ? 'cursor-pointer transition-colors hover:border-[#1681ba]/60 hover:bg-sky-50/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1681ba]/50'
                          : ''
                      }`}
                      role={isSelectable ? 'button' : undefined}
                      tabIndex={isSelectable ? 0 : undefined}
                      onClick={isSelectable ? () => onSelectProduct?.(link.parent_product_id) : undefined}
                      onKeyDown={
                        isSelectable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onSelectProduct?.(link.parent_product_id);
                              }
                            }
                          : undefined
                      }
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#1681ba]">
                          {t('supply.productDetail.bomExplosionPathDepth', { n: depth })}
                        </span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700">
                          {bomExplosionTypeShort(t, link.parent_product_type)}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
                        <span className="font-mono text-sm font-semibold text-gray-900">{link.parent_code}</span>
                        <span className="text-sm text-gray-600">{link.parent_name}</span>
                      </div>
                      <UpstreamLinkMetricsRow link={link} metrics={link.supply_metrics} t={t} />
                    </div>
                    {link.is_cycle_reference && (
                      <p className="mt-1 text-xs text-red-600">{t('supply.productDetail.bomExplosionCycle')}</p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BomExplosionPayloadView({
  payload,
  t,
  selectedProductId,
  onSelectProduct,
}: {
  payload: ProductBomExplosionPayload;
  t: TFunction;
  selectedProductId?: number;
  onSelectProduct?: (productId: number) => void;
}) {
  const { focus, parent_chains, downstream_children } = payload;

  const focusNode: ProductBomExplosionNode = {
    ...focus,
    children: [],
  };

  const hasUpstream = parent_chains.length > 0;
  const hasDownstream = downstream_children.length > 0;
  const hasOwnBom = focus.bom_version != null;
  const emptyExplosion = !hasUpstream && !hasOwnBom && !hasDownstream;

  return (
    <>
      {hasUpstream && (
        <BomExplosionParentChainsList
          parent_chains={parent_chains}
          t={t}
          selectedProductId={selectedProductId}
          onSelectProduct={onSelectProduct}
        />
      )}

      <BomExplosionNodeView node={focusNode} t={t} variant="focus" />

      {hasDownstream && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">
            {t('supply.productDetail.bomExplosionDownstreamTitle')}
          </h4>
          <div className="space-y-2">
            {downstream_children.map((child, idx) => (
              <BomExplosionNodeView
                key={`${child.product_id}-L${child.level}-i${idx}`}
                node={child}
                t={t}
                selectedProductId={selectedProductId}
                onSelectProduct={onSelectProduct}
              />
            ))}
          </div>
        </div>
      )}

      {emptyExplosion && (
        <p className="text-sm text-gray-500 mt-3">{t('supply.productDetail.bomExplosionNoBom')}</p>
      )}
    </>
  );
}

export function ProductDetailSidebar({
  product,
  isOpen,
  onClose,
  onPolicyUpdated,
  onBomTraceExternalManagedUpdated,
  onInTransitReceived,
  productionWeek,
  soExternalId,
}: ProductDetailSidebarProps) {
  const { t, i18n } = useTranslation();
  const { format: formatMoney } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState<ProductDetailResponse | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [visibleProduct, setVisibleProduct] = useState<SupplyTableProduct | null>(null);
  const [policyData, setPolicyData] = useState<InventoryPolicy | null>(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [policyForm, setPolicyForm] = useState<{ reorder_point: string; internal_safety_stock: string }>({
    reorder_point: '',
    internal_safety_stock: ''
  });
  const [policySaving, setPolicySaving] = useState(false);
  const [policySuccess, setPolicySuccess] = useState<string | null>(null);
  const [policyEditMode, setPolicyEditMode] = useState(false);
  const [salesViewMode, setSalesViewMode] = useState<'chart' | 'table'>('chart');
  const [traceUpdating, setTraceUpdating] = useState(false);
  const [markingReceivedId, setMarkingReceivedId] = useState<number | null>(null);
  const [receiveDraft, setReceiveDraft] = useState<{
    poiId: number;
    pendingQty: number;
    orderedQty: number;
    alreadyReceived: number;
  } | null>(null);
  const [receiveError, setReceiveError] = useState<string | null>(null);
  const [customerOrdersStatusFilter, setCustomerOrdersStatusFilter] = useState<
    'ALL' | 'PROGRAMMATO' | 'NON_PROGRAMMATO'
  >('ALL');
  const [customerOrdersPage, setCustomerOrdersPage] = useState(1);
  const [customerParentNettingPage, setCustomerParentNettingPage] = useState(1);
  const lastExternalProductIdRef = useRef<number | null>(null);
  const warehouseId = useWarehouseId();

  useEffect(() => {
    if (!product) {
      lastExternalProductIdRef.current = null;
      return;
    }
    const externalProductIdChanged = lastExternalProductIdRef.current !== product.id;
    lastExternalProductIdRef.current = product.id;
    if (externalProductIdChanged || !visibleProduct) {
      setVisibleProduct(product);
    }
  }, [product, visibleProduct]);

  const handleBomExplosionProductSelect = useCallback(
    async (productId: number) => {
      if (!Number.isFinite(productId)) return;
      if (visibleProduct?.id === productId) return;

      const baseParams = {
        product_id: productId,
        page: 1,
        page_size: 1,
        needs_reorder: false,
        // supply-table default is suggested_greater_zero=true; BOM parents/children
        // often have qty 0 and would be dropped, so the click looked like a no-op.
        suggested_greater_zero: false,
      };

      const fetchRow = async (extra: {
        warehouse_id?: number;
        production_week?: string;
        so_external_id?: string;
      } = {}) => {
        const response = await supplyService.getSupplyTableData({
          ...baseParams,
          ...extra,
        });
        return response.data?.[0];
      };

      try {
        const warehouseParam = warehouseId ? { warehouse_id: warehouseId } : {};
        const scopeParams = productionScopeQueryParams(productionWeek, soExternalId);

        let nextProduct =
          (await fetchRow({ ...warehouseParam, ...scopeParams })) ??
          (scopeParams.production_week || scopeParams.so_external_id
            ? await fetchRow(warehouseParam)
            : undefined) ??
          (await fetchRow(scopeParams)) ??
          (await fetchRow());

        if (!nextProduct) {
          const detail = await supplyService.getProductDetail(
            productId,
            warehouseId || undefined,
            undefined,
            productionWeek || soExternalId
              ? { production_week: productionWeek, so_external_id: soExternalId }
              : undefined,
          );
          if (!detail?.product) return;
          nextProduct = {
            id: detail.product.id,
            code: detail.product.code,
            name: detail.product.name,
            family: '',
            lifecycle_stage: { name: '', order: 0 },
            abc_category: '',
            stock_actual: 0,
            stock_en_transito: 0,
            venta_media_diaria: 0,
            reorder_point: 0,
            internal_safety_stock: 0,
            cantidad_sugerida_sistema: 0,
            cantidad_sugerida_usuario: null,
            suggested_quantity_id: null,
            supplier_id: null,
            supplier_name: '',
            lead_time: 0,
            moq: 0,
            mp: 0,
            last_purchase_price: 0,
            needs_reorder: false,
            unit_id: 0,
            unit_name: '',
            unit_abbreviation: '',
            supply_type: '',
            is_validated: false,
            available_suppliers: [],
          };
        }

        setVisibleProduct(nextProduct);
      } catch (error) {
        console.error('Error selecting BOM component product:', error);
      }
    },
    [visibleProduct?.id, warehouseId, productionWeek, soExternalId]
  );

  const loadProductDetail = useCallback(async () => {
    if (!visibleProduct) return;
    
    setLoading(true);
    try {
      const data = await supplyService.getProductDetail(
        visibleProduct.id,
        warehouseId || undefined,
        visibleProduct.supplier_id ?? undefined,
        productionWeek || soExternalId
          ? { production_week: productionWeek, so_external_id: soExternalId }
          : undefined,
      );
      setSalesData(data);
    } catch (error) {
      console.error('Error loading product detail:', error);
    } finally {
      setLoading(false);
    }
  }, [visibleProduct, warehouseId, productionWeek, soExternalId]);

  const handleTraceExternalManagedUpdate = useCallback(async (traceIds: number[], checked: boolean) => {
    if (!visibleProduct || traceIds.length === 0) return;
    const componentProductId = visibleProduct.id;
    setTraceUpdating(true);
    try {
      const result = await supplyService.bulkUpdateBomTraceExternalManaged(
        visibleProduct.id,
        { traceIds },
        checked
      );
      // Recargar solo lo necesario para que se reflejen los checkboxes,
      // pero sin refrescar `monthly_sales` para que el gráfico no cambie.
      const prevMonthlySales = salesData?.monthly_sales;
      const data = await supplyService.getProductDetail(
        visibleProduct.id,
        warehouseId || undefined,
        visibleProduct.supplier_id ?? undefined,
        productionWeek || soExternalId
          ? { production_week: productionWeek, so_external_id: soExternalId }
          : undefined,
      );
      setSalesData((prev) => ({
        ...data,
        // Preservar la serie usada por el gráfico.
        monthly_sales: prevMonthlySales ?? prev?.monthly_sales ?? data.monthly_sales
      }));
      if (onBomTraceExternalManagedUpdated) {
        await onBomTraceExternalManagedUpdated(componentProductId, result);
      }
    } catch (error) {
      console.error('Error updating externally managed traces:', error);
    } finally {
      setTraceUpdating(false);
    }
  }, [visibleProduct, warehouseId, salesData?.monthly_sales, onBomTraceExternalManagedUpdated, productionWeek, soExternalId]);

  const openReceiveDialog = useCallback((order: {
    id: number;
    quantity?: number;
    received_quantity?: number;
    pending_quantity?: number;
  }) => {
    if (markingReceivedId != null) return;
    const orderedQty = Number(order.quantity ?? 0);
    const alreadyReceived = Number(order.received_quantity ?? 0);
    const pendingQty = Number(
      order.pending_quantity ?? Math.max(0, orderedQty - alreadyReceived),
    );
    setReceiveError(null);
    setReceiveDraft({
      poiId: order.id,
      pendingQty,
      orderedQty,
      alreadyReceived,
    });
  }, [markingReceivedId]);

  const handleMarkInTransitReceived = useCallback(async (payload: {
    received_date: string;
    received_quantity: number | null;
  }) => {
    if (!visibleProduct || !receiveDraft || markingReceivedId != null) return;
    setMarkingReceivedId(receiveDraft.poiId);
    setReceiveError(null);
    try {
      await inventoryService.markPurchaseOrderItemReceived(receiveDraft.poiId, {
        received_date: payload.received_date,
        received_quantity: payload.received_quantity,
      });
      setReceiveDraft(null);
      // Recalculate puede fallar (502 engine) sin invalidar el mark-received:
      // igual hay que refrescar el detalle para sacar la línea de tránsito.
      try {
        await supplyService.recalculateProduct(visibleProduct.id, { force: true, skip_bom: false });
      } catch (recalcError) {
        console.error('Error recalculating after mark-received:', recalcError);
      }
      await loadProductDetail();
      if (onInTransitReceived) {
        await onInTransitReceived(visibleProduct.id);
      }
    } catch (error) {
      console.error('Error marking in-transit PO line as received:', error);
      const apiError = error as ApiError;
      setReceiveError(apiError.message || t('errors.serverError'));
    } finally {
      setMarkingReceivedId(null);
    }
  }, [visibleProduct, receiveDraft, markingReceivedId, loadProductDetail, onInTransitReceived, t]);

  const loadPolicyData = useCallback(async () => {
    if (!visibleProduct) return;

    if (typeof warehouseId !== 'number') {
      setPolicyData(null);
      setPolicyForm({ reorder_point: '', internal_safety_stock: '' });
      setPolicyError(t('supply.productDetail.selectWarehouseForPolicy'));
      return;
    }

    setPolicyLoading(true);
    try {
      console.log('Fetching policy for product:', visibleProduct.id, 'warehouse:', warehouseId);
      const data = await inventoryService.getCurrentPolicy(visibleProduct.id, warehouseId);
      console.log('Policy data received:', data);
      setPolicyData(data);
      setPolicyError(null);
    } catch (error: any) {
      console.error('Error fetching policy:', error);
      if (error.status === 404) {
        setPolicyData(null);
        setPolicyError(null);
      } else {
        setPolicyError(t('errors.fetchFailed'));
      }
    } finally {
      setPolicyLoading(false);
    }
  }, [visibleProduct, warehouseId, t]);

  const buildPolicyFormFromContext = useCallback((): {
    reorder_point: string;
    internal_safety_stock: string;
  } => ({
    reorder_point:
      policyData?.reorder_point !== null && policyData?.reorder_point !== undefined
        ? String(policyData.reorder_point)
        : (visibleProduct as any)?.inventory_policy__reorder_point !== undefined &&
            (visibleProduct as any)?.inventory_policy__reorder_point !== null
          ? String((visibleProduct as any).inventory_policy__reorder_point)
          : visibleProduct?.reorder_point !== undefined && visibleProduct?.reorder_point !== null
            ? String(visibleProduct.reorder_point)
            : policyData?.reorder_point_suggested !== undefined && policyData?.reorder_point_suggested !== null
              ? String(policyData.reorder_point_suggested)
              : (visibleProduct as any)?.inventory_policy__reorder_point_suggested !== undefined &&
                  (visibleProduct as any)?.inventory_policy__reorder_point_suggested !== null
                ? String((visibleProduct as any).inventory_policy__reorder_point_suggested)
                : '',
    internal_safety_stock:
      policyData?.internal_safety_stock !== null && policyData?.internal_safety_stock !== undefined
        ? String(policyData.internal_safety_stock)
        : policyData?.safety_stock !== null && policyData?.safety_stock !== undefined
          ? String(policyData.safety_stock)
          : (visibleProduct as any)?.inventory_policy__safety_stock !== undefined &&
              (visibleProduct as any)?.inventory_policy__safety_stock !== null
            ? String((visibleProduct as any).inventory_policy__safety_stock)
            : visibleProduct?.internal_safety_stock !== undefined && visibleProduct?.internal_safety_stock !== null
              ? String(visibleProduct.internal_safety_stock)
              : policyData?.safety_stock_suggested !== undefined && policyData?.safety_stock_suggested !== null
                ? String(policyData.safety_stock_suggested)
                : (visibleProduct as any)?.inventory_policy__safety_stock_suggested !== undefined &&
                    (visibleProduct as any)?.inventory_policy__safety_stock_suggested !== null
                  ? String((visibleProduct as any).inventory_policy__safety_stock_suggested)
                  : ''
  }), [policyData, visibleProduct]);

  const handlePolicyInputChange = useCallback((field: 'reorder_point' | 'internal_safety_stock', value: string) => {
    setPolicyForm(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const resetPolicyMessages = useCallback(() => {
    setPolicyError(null);
    setPolicySuccess(null);
  }, []);

  const handlePolicySave = useCallback(async () => {
    if (!visibleProduct) return;
    if (typeof warehouseId !== 'number') {
      setPolicyError(t('supply.productDetail.selectWarehouseToSave'));
      return;
    }

    const parseValue = (value: string): number | null => {
      if (value === '' || value === null) return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const reorderPointValue = parseValue(policyForm.reorder_point);
    const internalSafetyValue = parseValue(policyForm.internal_safety_stock);

    if (policyForm.reorder_point !== '' && reorderPointValue === null) {
      setPolicyError(t('supply.productDetail.enterValidReorderPoint'));
      return;
    }

    if (policyForm.internal_safety_stock !== '' && internalSafetyValue === null) {
      setPolicyError(t('supply.productDetail.enterValidSafetyStock'));
      return;
    }

    const payload: UpdateReorderPointRequest = {
      reorder_point: reorderPointValue,
      internal_safety_stock: internalSafetyValue
    };

    setPolicySaving(true);
    resetPolicyMessages();

    try {
      await inventoryService.updateReorderPoints(visibleProduct.id, payload, warehouseId);
      // Actualización optimista: muestra el valor nuevo inmediatamente
      // en el display de reorder_point sin esperar el pipeline externo.
      setVisibleProduct(prev => {
        if (!prev) return null;
        return {
          ...prev,
          reorder_point: reorderPointValue ?? (prev as any).reorder_point,
          inventory_policy__reorder_point: reorderPointValue ?? (prev as any).inventory_policy__reorder_point,
          internal_safety_stock: internalSafetyValue ?? (prev as any).internal_safety_stock,
          inventory_policy__safety_stock:
            internalSafetyValue ?? (prev as any).inventory_policy__safety_stock
        };
      });
      setPolicySuccess(t('supply.productDetail.policyUpdatedSuccessfully'));
      setPolicyEditMode(false);
      await loadPolicyData();
      if (onPolicyUpdated) {
        await onPolicyUpdated(visibleProduct.id);
      }
      setTimeout(() => setPolicySuccess(null), 4000);
    } catch (error) {
      console.error('Error updating policy:', error);
      setPolicyError(t('supply.productDetail.policyUpdateError'));
    } finally {
      setPolicySaving(false);
    }
  }, [visibleProduct, warehouseId, policyForm, resetPolicyMessages, loadPolicyData, onPolicyUpdated, t]);

  const handleCancelEdit = useCallback(() => {
    setPolicyEditMode(false);
    resetPolicyMessages();
    // Resetear el formulario a los valores de la política o vacío
    setPolicyForm(buildPolicyFormFromContext());
  }, [buildPolicyFormFromContext, resetPolicyMessages]);

  // Manejar el montaje/desmontaje del componente
  useEffect(() => {
    if (isOpen && (product || visibleProduct)) {
      setIsMounted(true);
    } else if (!isOpen && isMounted) {
      // Forzar un reflow accediendo al elemento del DOM antes de cambiar el estado
      const sidebar = document.querySelector('[data-sidebar]') as HTMLElement;
      if (sidebar) {
        // Forzar un reflow leyendo una propiedad del elemento
        void sidebar.offsetHeight;
      }
      // Usar doble requestAnimationFrame para asegurar que el navegador procese el cambio
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Iniciar la animación de salida
          setIsAnimating(false);
          // Luego esperar a que termine la animación antes de desmontar
          setTimeout(() => {
            setIsMounted(false);
            setSalesData(null);
            setVisibleProduct(null);
            setPolicyData(null);
            setPolicyForm({ reorder_point: '', internal_safety_stock: '' });
            setPolicyError(null);
            setPolicySuccess(null);
            setPolicyEditMode(false);
            setSalesViewMode('chart');
          }, 300);
        });
      });
    }
  }, [isOpen, product, visibleProduct, isMounted, loadPolicyData]);

  // Controlar la animación después de que el DOM se actualice
  useLayoutEffect(() => {
    if (isMounted && isOpen && (product || visibleProduct)) {
      // Forzar un reflow para asegurar que el estado inicial se aplique
      const sidebar = document.querySelector('[data-sidebar]');
      if (sidebar) {
        // Pequeño delay para permitir que el DOM se actualice
        const timer = setTimeout(() => {
          setIsAnimating(true);
        }, 10);
        return () => clearTimeout(timer);
      }
    }
  }, [isMounted, isOpen, product, visibleProduct]);

  // Cargar datos cuando el sidebar está abierto
  useEffect(() => {
    if (isOpen && visibleProduct && isMounted) {
      loadProductDetail();
      loadPolicyData();
    }
  }, [isOpen, visibleProduct, isMounted, loadProductDetail, loadPolicyData]);

  const currentYearMonthlySales = getCurrentYearMonthlyComparison(salesData?.monthly_sales);

  // Preparar datos para la gráfica: enero del año en curso vs mismo mes del año anterior
  const chartData = currentYearMonthlySales.map(item => ({
    month: item.month,
    [t('supply.productDetail.previousYear')]: item.previous_year,
    [t('supply.productDetail.currentYear')]: item.current_year
  }));

  // Datos para la tabla: mes actual primero, nombre completo del mes + año, variación y crecimiento
  const tableData = currentYearMonthlySales.length > 0
    ? (() => {
        const reversed = [...currentYearMonthlySales].reverse();
        return reversed.map((item, i, arr) => {
          const prevYear = item.previous_year;
          const currYear = item.current_year;
          const variacion =
            prevYear !== 0 ? ((currYear - prevYear) / prevYear) * 100 : null;
          const nextMonth = arr[i + 1];
          const crecimiento =
            nextMonth && nextMonth.current_year !== 0
              ? ((currYear - nextMonth.current_year) / nextMonth.current_year) * 100
              : null;
          const year = parseInt(item.month_key.split('-')[0], 10);
          const prevRowYear = i > 0 ? parseInt(arr[i - 1].month_key.split('-')[0], 10) : null;
          const yearSeparatorBefore = i > 0 && prevRowYear !== null && year !== prevRowYear;
          return {
            month: formatMonthAndYear(item.month_key, i18n.language),
            [t('supply.productDetail.previousYear')]: prevYear,
            [t('supply.productDetail.currentYear')]: currYear,
            variacion,
            crecimiento,
            yearSeparatorBefore
          };
        });
      })()
    : [];

  useEffect(() => {
    setCustomerOrdersPage(1);
  }, [salesData?.product?.id]);

  useEffect(() => {
    setCustomerOrdersPage(1);
  }, [customerOrdersStatusFilter]);
  useEffect(() => {
    setCustomerParentNettingPage(1);
  }, [salesData?.product?.id]);

  const customerOrdersPageSize = 12;
  const allCustomerOrdersRows = salesData?.customer_orders_summary?.rows ?? [];
  const customerOrdersRows =
    customerOrdersStatusFilter === 'ALL'
      ? allCustomerOrdersRows
      : allCustomerOrdersRows.filter((row) => row.planning_status === customerOrdersStatusFilter);
  const customerOrdersTotalPages = Math.max(1, Math.ceil(customerOrdersRows.length / customerOrdersPageSize));
  const safeCustomerOrdersPage = Math.min(customerOrdersPage, customerOrdersTotalPages);
  const customerOrdersStartIndex = (safeCustomerOrdersPage - 1) * customerOrdersPageSize;
  const customerOrdersPageRows = customerOrdersRows.slice(
    customerOrdersStartIndex,
    customerOrdersStartIndex + customerOrdersPageSize
  );
  const ordersTotalQty = customerOrdersRows.reduce(
    (acc, row) => acc + Number(row.qty_component ?? 0),
    0
  );
  const customerOrdersSummary = salesData?.customer_orders_summary;
  const demandByWeekRows = customerOrdersSummary?.demand_by_week ?? [];
  const coverageUntilArrival = customerOrdersSummary?.coverage_until_arrival;
  const parentNettingRowsAll = customerOrdersSummary?.parent_netting?.rows ?? [];
  const parentNettingPageSize = 10;
  const parentNettingTotalPages = Math.max(1, Math.ceil(parentNettingRowsAll.length / parentNettingPageSize));
  const safeParentNettingPage = Math.min(customerParentNettingPage, parentNettingTotalPages);
  const parentNettingStartIndex = (safeParentNettingPage - 1) * parentNettingPageSize;
  const parentNettingPageRows = parentNettingRowsAll.slice(
    parentNettingStartIndex,
    parentNettingStartIndex + parentNettingPageSize
  );
  const parentNettingShortageRows = parentNettingRowsAll.filter((r) => r.has_shortage).length;
  const expectedArrivalLeadTimeDays = Math.max(
    0,
    Number(salesData?.customer_orders_summary?.lead_time_days ?? visibleProduct?.lead_time ?? (visibleProduct as any)?.inventory_policy__lead_time_days ?? 0)
  );
  const selectedSupplier = visibleProduct?.available_suppliers?.find(
    (supplier) => supplier.supplier_id === visibleProduct.supplier_id
  ) ?? visibleProduct?.available_suppliers?.find((supplier) => supplier.is_primary);
  const selectedSupplierPurchaseFrequencyDays = Number(
    selectedSupplier?.purchase_frequency ??
    visibleProduct?.purchase_frequency ??
    visibleProduct?.purchase_frequency_days ??
    0
  );
  const suggestedQuantityTopKpi =
    visibleProduct?.production_scope?.net_post_moq ??
    salesData?.suggested_quantity_info?.cantidad_base ??
    visibleProduct?.cantidad_sugerida_usuario ??
    visibleProduct?.cantidad_sugerida_sistema ??
    0;

  const displayedStockInTransit = useMemo(() => {
    const fromDetail = inTransitQtyFromDetail(salesData);
    if (fromDetail !== null) return fromDetail;
    return Number(visibleProduct?.stock_en_transito ?? 0);
  }, [salesData, visibleProduct?.stock_en_transito]);

  if (!isMounted || !visibleProduct) return null;

  const sidebarContent = (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black z-[90] transition-opacity duration-300 ease-in-out ${
          isAnimating ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div
        data-sidebar
        className={`fixed right-0 inset-y-0 w-full md:w-1/2 bg-white shadow-xl z-[100] transform transition-transform duration-300 ease-in-out ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        } overflow-y-auto`}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{visibleProduct.name}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {visibleProduct.code} - {visibleProduct.supplier_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">{t('supply.productDetail.currentStock')}</p>
              <p className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                {visibleProduct.stock_actual}
                {(visibleProduct.has_fragmented_stock || salesData?.has_fragmented_stock) && (
                  <span title={t('supply.productDetail.fragmentedStockHint')} className="inline-flex">
                    <Package className="h-5 w-5 text-amber-600 shrink-0" aria-hidden />
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('supply.productDetail.inTransit')}: {displayedStockInTransit}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">{t('supply.productDetail.systemSuggested')}</p>
              {policySaving ? (
                <div className="flex items-center gap-2 h-9">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1681ba]" />
                  <span className="text-lg text-gray-400">{suggestedQuantityTopKpi}</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{suggestedQuantityTopKpi}</p>
              )}
              {visibleProduct.production_scope ? (
                <ul className="mt-2 space-y-0.5 text-[11px] text-gray-600">
                  <li>
                    {t('supply.productDetail.scopeGross')}: {visibleProduct.production_scope.gross}
                  </li>
                  <li>
                    {t('supply.productDetail.scopeCovered')}: {visibleProduct.production_scope.covered}
                  </li>
                  <li>
                    {t('supply.productDetail.scopeBuy')}: {visibleProduct.production_scope.net_post_moq}
                  </li>
                </ul>
              ) : null}
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">{t('supply.productDetail.estimatedMonthlySales')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {visibleProduct.consumo_mensual_estimado != null ? (
                  <>
                    {visibleProduct.consumo_mensual_estimado.toFixed(1)}
                    <span className="ml-1 text-base font-semibold text-gray-600">
                      {t('supply.productDetail.unitsPerMonth')}
                    </span>
                  </>
                ) : (
                  '—'
                )}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">{t('supply.productDetail.lastPrice')}</p>
              <p className="text-2xl font-bold text-gray-900">{formatMoney(visibleProduct.last_purchase_price)}</p>
            </div>
          </div>

          {visibleProduct.equivalence?.members &&
          visibleProduct.equivalence.members.length > 1 ? (
            <div className="mb-6 rounded-lg border border-sky-100 bg-sky-50/60 p-3">
              <p className="text-sm font-semibold text-gray-900">
                {t('inventoryPolicies.equivalence.sidebarTitle')}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                {t('inventoryPolicies.equivalence.sidebarHint')}
              </p>
              <ul className="mt-2 space-y-1 text-xs text-gray-700">
                {visibleProduct.equivalence.members.map((m) => (
                  <li
                    key={m.product_id}
                    className="flex items-center justify-between gap-2 border-b border-sky-100/80 pb-1 last:border-0 last:pb-0"
                  >
                    <span className="min-w-0 truncate font-mono">
                      {m.code}
                      {m.is_principal ? (
                        <span className="ml-1.5 font-sans text-[10px] font-medium text-sky-800">
                          ({t('inventoryPolicies.equivalence.principalMark')})
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 tabular-nums font-medium">{m.stock}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs font-semibold text-gray-900">
                {t('inventoryPolicies.equivalence.groupTotal', {
                  total: visibleProduct.equivalence.stock_group_total,
                })}
              </p>
              {visibleProduct.equivalence.conversion_pending ? (
                <p className="mt-1 text-[10px] leading-snug text-amber-700">
                  {t('inventoryPolicies.equivalence.conversionPending')}
                </p>
              ) : null}
            </div>
          ) : null}

          {salesData?.stock_lots && salesData.stock_lots.length > 0 ? (
            <StockLotsSection lots={salesData.stock_lots} t={t} locale={i18n.language} />
          ) : null}

          {/* Política de inventario (oculto: SHOW_REORDER_POINT_AND_SAFETY_STOCK_SECTION) */}
          {SHOW_REORDER_POINT_AND_SAFETY_STOCK_SECTION && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('supply.productDetail.reorderPointAndSafetyStock')}</h3>
              </div>
              {policyEditMode ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                    disabled={policySaving}
                  >
                    {t('supply.productDetail.cancel')}
                  </button>
                  <button
                    onClick={handlePolicySave}
                    disabled={policySaving}
                    className="px-4 py-2 text-sm bg-[#1681ba] text-white rounded-md hover:bg-[#136a98] transition-colors disabled:opacity-50"
                  >
                    {policySaving ? t('supply.productDetail.saving') : t('supply.productDetail.save')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    resetPolicyMessages();
                    // Inicializar el formulario con los valores de la política o vacío
                    setPolicyForm(buildPolicyFormFromContext());
                    setPolicyEditMode(true);
                  }}
                  disabled={policyLoading || typeof warehouseId !== 'number'}
                  className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {t('supply.productDetail.edit')}
                </button>
              )}
            </div>

            {policyLoading ? (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]"></div>
              </div>
            ) : policyError ? (
              <p className="text-sm text-red-600">{policyError}</p>
            ) : (
              <>
                {policySuccess && (
                  <div className="mb-4 rounded-md bg-green-50 px-4 py-2 text-sm text-green-700">
                    {policySuccess}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('supply.productDetail.reorderPoint')}</p>
                    {policyEditMode ? (
                      <input
                        type="number"
                        min={0}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1681ba] focus:border-[#1681ba]"
                        value={policyForm.reorder_point}
                        onChange={(e) => handlePolicyInputChange('reorder_point', e.target.value)}
                      />
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-gray-900">
                          {(() => {
                            const val = policyData?.reorder_point !== null && policyData?.reorder_point !== undefined
                              ? policyData.reorder_point
                              : (visibleProduct as any)?.inventory_policy__reorder_point !== null && (visibleProduct as any)?.inventory_policy__reorder_point !== undefined
                                ? (visibleProduct as any).inventory_policy__reorder_point
                                : visibleProduct?.reorder_point !== null && visibleProduct?.reorder_point !== undefined
                                  ? visibleProduct.reorder_point
                                  : (policyData?.reorder_point_suggested !== null && policyData?.reorder_point_suggested !== undefined)
                                    ? policyData.reorder_point_suggested
                                    : ((visibleProduct as any)?.inventory_policy__reorder_point_suggested !== null && (visibleProduct as any)?.inventory_policy__reorder_point_suggested !== undefined)
                                      ? (visibleProduct as any).inventory_policy__reorder_point_suggested
                                      : '—';
                            return val;
                          })()}
                        </p>
                        {(() => {
                          const fromInvPolicies = visibleProduct?.__fromInventoryPolicies === true;
                          const isAssigned =
                            (policyData?.reorder_point !== null && policyData?.reorder_point !== undefined) ||
                            ((visibleProduct as any)?.inventory_policy__reorder_point !== null &&
                              (visibleProduct as any)?.inventory_policy__reorder_point !== undefined) ||
                            (!fromInvPolicies &&
                              visibleProduct?.reorder_point !== null &&
                              visibleProduct?.reorder_point !== undefined);

                          const hasSuggested =
                            (policyData?.reorder_point_suggested !== null &&
                              policyData?.reorder_point_suggested !== undefined) ||
                            ((visibleProduct as any)?.inventory_policy__reorder_point_suggested !== null &&
                              (visibleProduct as any)?.inventory_policy__reorder_point_suggested !== undefined);

                          if (isAssigned) {
                            return (
                              <p className="text-xs text-green-600 mt-1 font-medium italic">
                                Valor asignado
                              </p>
                            );
                          } else if (hasSuggested) {
                            return (
                              <p className="text-xs text-blue-600 mt-1 font-medium italic">
                                Valor sugerido
                              </p>
                            );
                          }
                          return null;
                        })()}
                      </>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('supply.productDetail.safetyStock')}</p>
                    {policyEditMode ? (
                      <input
                        type="number"
                        min={0}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1681ba] focus:border-[#1681ba]"
                        value={policyForm.internal_safety_stock || ''}
                        onChange={(e) => handlePolicyInputChange('internal_safety_stock', e.target.value)}
                      />
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-gray-900">
                          {(() => {
                            const val = policyData?.internal_safety_stock !== null && policyData?.internal_safety_stock !== undefined
                              ? policyData.internal_safety_stock
                              : policyData?.safety_stock !== null && policyData?.safety_stock !== undefined
                                ? policyData.safety_stock
                                : (visibleProduct as any)?.inventory_policy__safety_stock !== null && (visibleProduct as any)?.inventory_policy__safety_stock !== undefined
                                  ? (visibleProduct as any).inventory_policy__safety_stock
                                  : visibleProduct?.internal_safety_stock !== null && visibleProduct?.internal_safety_stock !== undefined
                                    ? visibleProduct.internal_safety_stock
                                    : (policyData?.safety_stock_suggested !== null && policyData?.safety_stock_suggested !== undefined)
                                      ? policyData.safety_stock_suggested
                                      : ((visibleProduct as any)?.inventory_policy__safety_stock_suggested !== null && (visibleProduct as any)?.inventory_policy__safety_stock_suggested !== undefined)
                                        ? (visibleProduct as any).inventory_policy__safety_stock_suggested
                                        : '—';
                            return val;
                          })()}
                        </p>
                        {(() => {
                          const fromInvPolicies = visibleProduct?.__fromInventoryPolicies === true;
                          const isAssigned =
                            (policyData?.internal_safety_stock !== null &&
                              policyData?.internal_safety_stock !== undefined) ||
                            (policyData?.safety_stock !== null && policyData?.safety_stock !== undefined) ||
                            ((visibleProduct as any)?.inventory_policy__safety_stock !== null &&
                              (visibleProduct as any)?.inventory_policy__safety_stock !== undefined) ||
                            (!fromInvPolicies &&
                              visibleProduct?.internal_safety_stock !== null &&
                              visibleProduct?.internal_safety_stock !== undefined);

                          const hasSuggested =
                            (policyData?.safety_stock_suggested !== null &&
                              policyData?.safety_stock_suggested !== undefined) ||
                            ((visibleProduct as any)?.inventory_policy__safety_stock_suggested !== null &&
                              (visibleProduct as any)?.inventory_policy__safety_stock_suggested !== undefined);

                          if (isAssigned) {
                            return (
                              <p className="text-xs text-green-600 mt-1 font-medium italic">
                                Valor asignado
                              </p>
                            );
                          } else if (hasSuggested) {
                            return (
                              <p className="text-xs text-blue-600 mt-1 font-medium italic">
                                Valor sugerido
                              </p>
                            );
                          }
                          return null;
                        })()}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          )}

          {/* Gráfica de Ventas Mensuales */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('supply.productDetail.monthlySalesComparison')}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {t('supply.productDetail.monthlySalesDescription')}
                </p>
              </div>
              {!loading && salesData && chartData.length > 0 && (
                <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setSalesViewMode('chart')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      salesViewMode === 'chart'
                        ? 'bg-white text-[#1681ba] shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <BarChart2 className="h-4 w-4" />
                    {t('supply.productDetail.viewChart')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSalesViewMode('table')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      salesViewMode === 'table'
                        ? 'bg-white text-[#1681ba] shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Table2 className="h-4 w-4" />
                    {t('supply.productDetail.viewTable')}
                  </button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]"></div>
              </div>
            ) : salesData && chartData.length > 0 ? (
              <>
                {salesViewMode === 'chart' ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey={t('supply.productDetail.previousYear')} fill="#9ca3af" />
                      <Bar dataKey={t('supply.productDetail.currentYear')} fill="#1681ba" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {t('supply.productDetail.monthAndYear')}
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {t('supply.productDetail.previousYear')}
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {t('supply.productDetail.currentYear')}
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {t('supply.productDetail.variation')}
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {t('supply.productDetail.growthVsPreviousMonth')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tableData.map((row, idx) => (
                          <Fragment key={idx}>
                            {row.yearSeparatorBefore && (
                              <tr aria-hidden className="border-0">
                                <td
                                  colSpan={5}
                                  className="p-0 h-0 align-baseline border-0 border-t border-black"
                                />
                              </tr>
                            )}
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                                {row.month}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 text-right">
                                {row[t('supply.productDetail.previousYear')]}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 text-right">
                                {row[t('supply.productDetail.currentYear')]}
                              </td>
                              <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                                {row.variacion != null ? (
                                  <span
                                    className={
                                      row.variacion >= 0
                                        ? 'text-green-600 font-medium'
                                        : 'text-red-600 font-medium'
                                    }
                                  >
                                    {row.variacion >= 0 ? '+' : ''}
                                    {row.variacion.toFixed(1)}%
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                                {row.crecimiento != null ? (
                                  <span
                                    className={
                                      row.crecimiento >= 0
                                        ? 'text-green-600 font-medium'
                                        : 'text-red-600 font-medium'
                                    }
                                  >
                                    {row.crecimiento >= 0 ? '+' : ''}
                                    {row.crecimiento.toFixed(1)}%
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totales y Crecimiento */}
                {salesData.totals && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-gray-700">{t('supply.productDetail.kpiTotals')}</h4>
                      {/* Icono de información con tooltip al hover */}
                      <div className="relative group">
                        <Info className="h-4 w-4 text-gray-400 hover:text-blue-600 cursor-help transition-colors" />
                        {/* Tooltip */}
                        <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                          <p className="leading-relaxed">
                            {t('supply.productDetail.kpiCalculationInfo')}
                          </p>
                          {/* Flecha del tooltip */}
                          <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{t('supply.productDetail.totalCurrentYear')}</p>
                        <p className="text-xl font-bold text-gray-900">{salesData.totals.current_year}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{t('supply.productDetail.totalPreviousYear')}</p>
                        <p className="text-xl font-bold text-gray-900">{salesData.totals.previous_year}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{t('supply.productDetail.growth')}</p>
                        <p className={`text-xl font-bold ${
                          salesData.totals.growth_percentage >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {salesData.totals.growth_percentage >= 0 ? '+' : ''}{salesData.totals.growth_percentage}%
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                {t('supply.productDetail.noSalesData')}
              </div>
            )}
          </div>

          {/* Órdenes en tránsito: siempre que exista detalle cargado */}
          {salesData && (() => {
            const inTransitOrders = salesData.in_transit_orders ?? [];
            const showClientOrdersColumn = salesData.is_on_demand === true;
            const footerLabelColSpan = showClientOrdersColumn ? 4 : 3;
            const inTransitAllocatedTotal = inTransitOrders.reduce(
              (s, o) => s + Number(o.allocated_quantity ?? 0),
              0
            );
            const inTransitQuantityTotal = inTransitOrders.reduce((s, o) => s + Number(o.quantity ?? 0), 0);
            const inTransitPendingTotal = inTransitOrders.reduce(
              (s, o) => s + Number(o.pending_quantity ?? Math.max(0, Number(o.quantity ?? 0) - Number(o.received_quantity ?? 0))),
              0
            );
            return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('supply.productDetail.inTransitTitle')}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {t('supply.productDetail.inTransitDescription')}
                </p>
              </div>
              {inTransitOrders.length > 0 ? (
                <>
                <div className="overflow-x-auto rounded-md border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                          {t('supply.productDetail.inTransitOrderNumber')}
                        </th>
                        <th scope="col" className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                          {t('supply.productDetail.inTransitSupplier')}
                        </th>
                        {showClientOrdersColumn && (
                          <th scope="col" className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                            {t('supply.productDetail.inTransitSalesOrders')}
                          </th>
                        )}
                        {showClientOrdersColumn && (
                          <th scope="col" className="px-2.5 py-1.5 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                            {t('supply.productDetail.inTransitAllocatedQuantity')}
                          </th>
                        )}
                        <th scope="col" className="px-2.5 py-1.5 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                          {showClientOrdersColumn
                            ? t('supply.productDetail.inTransitQuantity')
                            : t('supply.productDetail.inTransitPendingQuantity')}
                        </th>
                        {showClientOrdersColumn && (
                          <th scope="col" className="px-2.5 py-1.5 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                            {t('supply.productDetail.inTransitPendingQuantity')}
                          </th>
                        )}
                        <th scope="col" className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                          {t('supply.productDetail.inTransitOrderDate')}
                        </th>
                        <th scope="col" className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                          {t('supply.productDetail.inTransitExpectedDate')}
                        </th>
                        <th scope="col" className="px-2.5 py-1.5 text-center text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                          {t('supply.productDetail.inTransitStatus')}
                        </th>
                        <th scope="col" className="px-2.5 py-1.5 text-center text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                          {t('supply.productDetail.inTransitMarkReceived')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        const today = new Date();
                        const dayOfWeek = today.getDay();
                        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                        const currentWeekMonday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diffToMonday);
                        const nextWeekMonday = new Date(currentWeekMonday.getTime() + 7 * 24 * 60 * 60 * 1000);
                        const weekAfterNextMonday = new Date(currentWeekMonday.getTime() + 14 * 24 * 60 * 60 * 1000);

                        const getWeekInfo = (dateStr: string | null) => {
                          if (!dateStr) return { label: '—', isThisWeek: false, isPastWeek: false, daysOverdue: 0 };
                          const d = new Date(dateStr);
                          const isThisWeek = d >= currentWeekMonday && d < nextWeekMonday;
                          const isPastWeek = d < currentWeekMonday;
                          const daysOverdue = isPastWeek ? Math.floor((currentWeekMonday.getTime() - d.getTime()) / (24 * 60 * 60 * 1000)) : 0;
                          let label = dateStr;
                          if (isThisWeek) {
                            label = t('supply.productDetail.inTransitThisWeek');
                          } else if (d >= nextWeekMonday && d < weekAfterNextMonday) {
                            label = t('supply.productDetail.inTransitNextWeek');
                          }
                          return { label, isThisWeek, isPastWeek, daysOverdue };
                        };

                        return inTransitOrders.map((order) => {
                          const weekInfo = getWeekInfo(order.expected_date);
                          return (
                            <tr
                              key={order.id}
                              className={`hover:bg-gray-50 ${weekInfo.isPastWeek ? 'bg-red-50' : weekInfo.isThisWeek ? 'bg-amber-50' : ''}`}
                            >
                              <td className="px-2.5 py-1.5 text-xs font-medium text-gray-900 whitespace-nowrap">
                                {order.order_number}
                              </td>
                              <td className="px-2.5 py-1.5 text-xs text-gray-700 whitespace-nowrap">
                                {order.supplier_name ?? '—'}
                              </td>
                              {showClientOrdersColumn && (
                                <td className="px-2.5 py-1.5 text-xs text-gray-700">
                                  {order.sales_orders && order.sales_orders.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {order.sales_orders.map((so) => (
                                        <span
                                          key={so}
                                          className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs"
                                        >
                                          {so}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              )}
                              {showClientOrdersColumn && (
                                <td className="px-2.5 py-1.5 text-xs text-gray-700 text-right whitespace-nowrap">
                                  {Number(order.allocated_quantity ?? 0).toFixed(2)}
                                </td>
                              )}
                              <td className="px-2.5 py-1.5 text-xs text-gray-700 text-right whitespace-nowrap">
                                {showClientOrdersColumn
                                  ? Number(order.quantity ?? 0).toFixed(2)
                                  : Number(order.pending_quantity ?? Math.max(0, Number(order.quantity ?? 0) - Number(order.received_quantity ?? 0))).toFixed(2)}
                              </td>
                              {showClientOrdersColumn && (
                                <td className="px-2.5 py-1.5 text-xs text-gray-700 text-right whitespace-nowrap">
                                  {Number(order.pending_quantity ?? Math.max(0, Number(order.quantity ?? 0) - Number(order.received_quantity ?? 0))).toFixed(2)}
                                </td>
                              )}
                              <td className="px-2.5 py-1.5 text-xs text-gray-700 whitespace-nowrap">
                                {order.order_date ?? '—'}
                              </td>
                              <td className={`px-2.5 py-1.5 text-xs whitespace-nowrap ${weekInfo.isPastWeek ? 'text-red-600 font-medium' : weekInfo.isThisWeek ? 'text-amber-600 font-medium' : 'text-gray-700'}`}>
                                {weekInfo.label}
                              </td>
                              <td className="px-2.5 py-1.5 text-xs text-center whitespace-nowrap">
                                {weekInfo.isPastWeek ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                    {t('supply.productDetail.inTransitOverdue')}
                                    {weekInfo.daysOverdue > 0 && (
                                      <span className="ml-1 text-red-600">
                                        ({t('supply.productDetail.inTransitDaysOverdue', { days: weekInfo.daysOverdue })})
                                      </span>
                                    )}
                                  </span>
                                ) : weekInfo.isThisWeek ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                    {t('supply.productDetail.inTransitPendingReceive')}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                    {t('supply.productDetail.inTransitOnTime')}
                                  </span>
                                )}
                              </td>
                              <td className="px-2.5 py-1.5 text-xs text-center whitespace-nowrap">
                                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                                    checked={false}
                                    disabled={markingReceivedId === order.id}
                                    onChange={() => {
                                      openReceiveDialog(order);
                                    }}
                                    title={t('supply.productDetail.inTransitMarkReceivedHint')}
                                  />
                                  <span className="text-[10px] text-gray-600">
                                    {markingReceivedId === order.id
                                      ? t('common.loading')
                                      : t('supply.productDetail.inTransitArrived')}
                                  </span>
                                </label>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                    <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                      <tr>
                        <td colSpan={footerLabelColSpan} className="px-2.5 py-1.5 text-[10px] font-semibold text-gray-700">
                          {t('supply.productDetail.bomTraceTotals')}
                        </td>
                        {showClientOrdersColumn && (
                          <td className="px-2.5 py-1.5 text-[10px] font-semibold text-right text-gray-900">
                            {inTransitAllocatedTotal.toFixed(2)}
                          </td>
                        )}
                        <td className="px-2.5 py-1.5 text-[10px] font-semibold text-right text-gray-900">
                          {showClientOrdersColumn
                            ? inTransitQuantityTotal.toFixed(2)
                            : inTransitPendingTotal.toFixed(2)}
                        </td>
                        {showClientOrdersColumn && (
                          <td className="px-2.5 py-1.5 text-[10px] font-semibold text-right text-gray-900">
                            {inTransitPendingTotal.toFixed(2)}
                          </td>
                        )}
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
                </>
              ) : (
                <p className="text-sm text-gray-500 py-4">
                  {t('supply.productDetail.inTransitNoOrders')}
                </p>
              )}
              {showClientOrdersColumn && (salesData.in_transit_unassigned_total ?? 0) > 0 && (
                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  {t('supply.productDetail.inTransitUnassignedWarning', {
                    qty: Number(salesData.in_transit_unassigned_total ?? 0).toFixed(2)
                  })}
                </div>
              )}
              {showClientOrdersColumn && (salesData.in_transit_partially_unassigned_total ?? 0) > 0 && (
                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  {t('supply.productDetail.inTransitPartiallyUnassignedWarning', {
                    qty: Number(salesData.in_transit_partially_unassigned_total ?? 0).toFixed(2)
                  })}
                </div>
              )}
            </div>
            );
          })()}

          {salesData?.product?.product_type !== 'FINISHED_DIST' &&
            (salesData?.customer_orders_summary || salesData?.is_on_demand) && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('supply.productDetail.consumptionAnalysisTitle')}
                  </h3>
                  <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                    {visibleProduct.code} - {visibleProduct.name}
                  </span>
                </div>
                {(() => {
                  const inLtPlanned = Number(customerOrdersSummary?.leadtime_window_totals?.planned_qty ?? 0);
                  const inLtNonPlanned = Number(customerOrdersSummary?.leadtime_window_totals?.non_planned_qty ?? 0);
                  const totalPlanned = Number(customerOrdersSummary?.totals?.planned_qty ?? 0);
                  const totalNonPlanned = Number(customerOrdersSummary?.totals?.non_planned_qty ?? 0);
                  const ltStartWeekLabel = customerOrdersSummary?.arrival_week
                    ? isoWeekLabel(customerOrdersSummary.arrival_week)
                    : '—';
                  const ltEndWeekLabel = customerOrdersSummary?.lead_time_window_end
                    ? isoWeekLabel(customerOrdersSummary.lead_time_window_end)
                    : '—';
                  const laterPlanned = Math.max(0, totalPlanned - inLtPlanned);
                  const laterNonPlanned = Math.max(0, totalNonPlanned - inLtNonPlanned);
                  const suggestedBase =
                    salesData.suggested_quantity_info?.cantidad_base ??
                    (visibleProduct.cantidad_sugerida_usuario ?? visibleProduct.cantidad_sugerida_sistema ?? 0);
                  const suggestedAdjusted =
                    salesData.suggested_quantity_info?.cantidad_ajustada ??
                    (visibleProduct.cantidad_sugerida_usuario ?? visibleProduct.cantidad_sugerida_sistema ?? 0);
                  const suggestedMoq = salesData.suggested_quantity_info?.moq ?? visibleProduct.moq;
                  const suggestedMp = salesData.suggested_quantity_info?.mp ?? visibleProduct.mp;
                  const today = new Date();
                  const forecastStartWeekLabel = isoWeekLabel(
                    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
                  );
                  const forecastEndWeekLabel = coverageUntilArrival?.forecast_period_end
                    ? isoWeekLabel(coverageUntilArrival.forecast_period_end)
                    : '—';
                  return (
                    <>
                      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-4">
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
                          <span className="whitespace-nowrap">
                            {t('supply.productDetail.customerSummaryStockActual')}:{' '}
                            <strong className="text-gray-900">{visibleProduct.stock_actual}</strong>
                          </span>
                          <span className="text-gray-300" aria-hidden>
                            |
                          </span>
                          <span className="whitespace-nowrap">
                            {t('supply.productDetail.customerSummaryInTransit')}:{' '}
                            <strong className="text-gray-900">{displayedStockInTransit}</strong>
                          </span>
                          <span className="text-gray-300" aria-hidden>
                            |
                          </span>
                          <span className="whitespace-nowrap">
                            {t('supply.productDetail.customerSummarySuggestedQty')}:{' '}
                            <strong className="text-gray-900">{suggestedBase}</strong>{' '}
                            <span className="text-xs text-gray-500">
                              ({t('supply.productDetail.customerSummaryConstraints', {
                                moq: suggestedMoq,
                                mp: suggestedMp,
                                adjusted: suggestedAdjusted,
                              })})
                            </span>
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-gray-700">
                          {t('supply.productDetail.customerSummaryLeadTime')}:{' '}
                          <strong className="text-gray-900">{Number(coverageUntilArrival?.lead_time_days ?? expectedArrivalLeadTimeDays)} gg</strong>
                          <span className="mx-2 text-gray-300" aria-hidden>
                            |
                          </span>
                          {t('supply.productDetail.customerSummaryPurchaseFrequency')}:{' '}
                          <strong className="text-gray-900">
                            {selectedSupplierPurchaseFrequencyDays > 0
                              ? `${selectedSupplierPurchaseFrequencyDays} gg`
                              : '—'}
                          </strong>
                        </div>
                        {!salesData?.is_on_demand && (
                          <>
                            <div className="mt-1 text-xs text-gray-700">
                              {t('supply.productDetail.customerSummaryForecastPeriodLtr', {
                                todayWeek: forecastStartWeekLabel,
                                ltrWeek: forecastEndWeekLabel,
                              })}
                              :{' '}
                              <strong className="text-gray-900">
                                {Math.round(Number(coverageUntilArrival?.forecast_period_qty ?? 0))}
                              </strong>
                            </div>
                            <div className="mt-1 text-xs text-gray-700">
                              {t('supply.productDetail.customerSummarySafetyStock')}:{' '}
                              <strong className="text-gray-900">
                                {visibleProduct.internal_safety_stock ?? 0}
                              </strong>
                            </div>
                          </>
                        )}
                      </div>

                      {!salesData?.is_on_demand &&
                        coverageUntilArrival &&
                        customerOrdersSummary?.source_mode !== 'COMPONENT' && (
                        <div className="mb-4 overflow-x-auto rounded-lg border border-gray-200">
                          <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                            {t('supply.productDetail.coverageUntilArrivalTitle')}
                          </div>
                          <table className="min-w-full divide-y divide-gray-200">
                            <tbody className="bg-white divide-y divide-gray-200">
                              <tr>
                                <td className="px-2.5 py-1.5 text-xs text-gray-700">{t('supply.productDetail.coverageArrivalDate')}</td>
                                <td className="px-2.5 py-1.5 text-xs text-right font-semibold text-gray-900">{coverageUntilArrival.arrival_date}</td>
                              </tr>
                              <tr>
                                <td className="px-2.5 py-1.5 text-xs text-gray-700">{t('supply.productDetail.coverageCurrentStock')}</td>
                                <td className="px-2.5 py-1.5 text-xs text-right font-semibold text-gray-900">{coverageUntilArrival.stock_actual.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td className="px-2.5 py-1.5 text-xs text-gray-700">{t('supply.productDetail.coverageTransitUntilArrival')}</td>
                                <td className="px-2.5 py-1.5 text-xs text-right font-semibold text-gray-900">{coverageUntilArrival.stock_in_transit.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td className="px-2.5 py-1.5 text-xs text-gray-700">
                                  {t('supply.productDetail.coverageConsumptionRequiredUntilArrival')}
                                </td>
                                <td className="px-2.5 py-1.5 text-xs text-right font-semibold text-gray-900">
                                  {coverageUntilArrival.consumption_required_until_arrival.toFixed(2)}
                                </td>
                              </tr>
                              <tr className={coverageUntilArrival.net_available_at_arrival < 0 ? 'bg-red-50' : 'bg-emerald-50'}>
                                <td className="px-2.5 py-1.5 text-xs text-gray-800 font-semibold">{t('supply.productDetail.coverageNetAtArrival')}</td>
                                <td
                                  className={`px-2.5 py-1.5 text-xs text-right font-bold ${
                                    coverageUntilArrival.net_available_at_arrival < 0 ? 'text-red-700' : 'text-emerald-700'
                                  }`}
                                >
                                  {coverageUntilArrival.net_available_at_arrival.toFixed(2)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        )}

                      <div className="mb-4 overflow-x-auto rounded-md border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2.5 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                                {t('supply.productDetail.customerStatus')}
                              </th>
                              <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                                {`${ltStartWeekLabel} -> ${ltEndWeekLabel}`}
                              </th>
                              <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                                {`> ${ltEndWeekLabel}`}
                              </th>
                              <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                                {t('supply.productDetail.customerTotal')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            <tr>
                              <td className="px-2.5 py-1.5 text-xs font-medium text-gray-900">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCustomerOrdersStatusFilter((prev) =>
                                      prev === 'PROGRAMMATO' ? 'ALL' : 'PROGRAMMATO'
                                    )
                                  }
                                  className={`rounded px-1.5 py-0.5 transition-colors ${
                                    customerOrdersStatusFilter === 'PROGRAMMATO'
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : 'hover:bg-gray-100'
                                  }`}
                                  title={t('supply.productDetail.customerFilterProgrammed')}
                                >
                                  PROGRAMMATO
                                </button>
                              </td>
                              <td className="px-2.5 py-1.5 text-xs text-right text-gray-700">{inLtPlanned.toFixed(2)}</td>
                              <td className="px-2.5 py-1.5 text-xs text-right text-gray-700">{laterPlanned.toFixed(2)}</td>
                              <td className="px-2.5 py-1.5 text-xs text-right font-semibold text-gray-900">{totalPlanned.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td className="px-2.5 py-1.5 text-xs font-medium text-gray-900">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCustomerOrdersStatusFilter((prev) =>
                                      prev === 'NON_PROGRAMMATO' ? 'ALL' : 'NON_PROGRAMMATO'
                                    )
                                  }
                                  className={`rounded px-1.5 py-0.5 transition-colors ${
                                    customerOrdersStatusFilter === 'NON_PROGRAMMATO'
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'hover:bg-gray-100'
                                  }`}
                                  title={t('supply.productDetail.customerFilterNonProgrammed')}
                                >
                                  NON PROGRAMMATO
                                </button>
                              </td>
                              <td className="px-2.5 py-1.5 text-xs text-right text-gray-700">{inLtNonPlanned.toFixed(2)}</td>
                              <td className="px-2.5 py-1.5 text-xs text-right text-gray-700">{laterNonPlanned.toFixed(2)}</td>
                              <td className="px-2.5 py-1.5 text-xs text-right font-semibold text-gray-900">{totalNonPlanned.toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      {customerOrdersStatusFilter !== 'ALL' && (
                        <div className="mb-2">
                          <button
                            type="button"
                            onClick={() => setCustomerOrdersStatusFilter('ALL')}
                            className="text-[10px] text-blue-700 hover:text-blue-900 underline"
                          >
                            {t('supply.productDetail.customerRemoveStatusFilter', {
                              status: customerOrdersStatusFilter,
                            })}
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}

                {(customerOrdersSummary?.parent_netting?.rows?.length ?? 0) > 0 && (
                  <div className="mb-4 overflow-x-auto rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        {t('supply.productDetail.parentNettingTitle')}
                      </span>
                      <span className="text-[11px] text-gray-600">
                        {t('supply.productDetail.parentNettingSummary', {
                          total: customerOrdersSummary?.parent_netting?.total_parents ?? 0,
                          gap: parentNettingShortageRows,
                        })}
                      </span>
                    </div>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2.5 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            {t('supply.productDetail.parentNettingParent')}
                          </th>
                          <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            {t('supply.productDetail.parentNettingAvailableStock')}
                          </th>
                          <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            {t('supply.productDetail.parentNettingInTransit')}
                          </th>
                          <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            {t('supply.productDetail.parentNettingRequiredQty')}
                          </th>
                          <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            {t('supply.productDetail.parentNettingGap')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parentNettingPageRows.map((row) => (
                          <tr key={row.parent_product_id} className={row.has_shortage ? 'bg-red-50 hover:bg-red-100/50' : 'hover:bg-gray-50'}>
                            <td className="px-2.5 py-1.5 text-xs text-gray-800">
                              <div className="font-medium">{row.parent_code ?? '—'}</div>
                              <div className="text-[11px] text-gray-500">{row.parent_name ?? ''}</div>
                            </td>
                            <td className="px-2.5 py-1.5 text-xs text-right text-gray-700">{row.stock_available.toFixed(2)}</td>
                            <td className="px-2.5 py-1.5 text-xs text-right text-gray-700">{Number(row.stock_in_transit ?? 0).toFixed(2)}</td>
                            <td className="px-2.5 py-1.5 text-xs text-right text-gray-700">
                              <div className="font-semibold text-gray-900">{row.required_qty.toFixed(2)}</div>
                              <div className="mt-0.5 text-[10px] leading-4 text-gray-600">
                                <div>
                                  {t('supply.productDetail.parentNettingBeforeLtShort')} P{' '}
                                  {Number(row.required_breakdown?.before_lt?.planned_qty ?? 0).toFixed(2)} / NP{' '}
                                  {Number(row.required_breakdown?.before_lt?.non_planned_qty ?? 0).toFixed(2)}
                                </div>
                                <div>
                                  {t('supply.productDetail.parentNettingAfterLtShort')} P{' '}
                                  {Number(row.required_breakdown?.after_lt?.planned_qty ?? 0).toFixed(2)} / NP{' '}
                                  {Number(row.required_breakdown?.after_lt?.non_planned_qty ?? 0).toFixed(2)}
                                </div>
                              </div>
                            </td>
                            <td className={`px-2.5 py-1.5 text-xs text-right font-semibold ${row.has_shortage ? 'text-red-700' : 'text-emerald-700'}`}>
                              {row.shortage_qty.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                      <span>
                        {t('supply.productDetail.paginationShowingOf', {
                          from: parentNettingStartIndex + 1,
                          to: Math.min(parentNettingStartIndex + parentNettingPageRows.length, parentNettingRowsAll.length),
                          total: parentNettingRowsAll.length,
                        })}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCustomerParentNettingPage((p) => Math.max(1, p - 1))}
                          disabled={safeParentNettingPage <= 1}
                          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {t('supply.productDetail.paginationPrev')}
                        </button>
                        <span>
                          {safeParentNettingPage}/{parentNettingTotalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setCustomerParentNettingPage((p) => Math.min(parentNettingTotalPages, p + 1))}
                          disabled={safeParentNettingPage >= parentNettingTotalPages}
                          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {t('supply.productDetail.paginationNext')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {demandByWeekRows.length > 0 && (
                  <div className="mb-4 overflow-x-auto rounded-lg border border-gray-200">
                    <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                      {t('supply.productDetail.customerWeeklyDemandTitle')}
                    </div>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2.5 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            {t('supply.productDetail.customerWeek')}
                          </th>
                          <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            {t('supply.productDetail.customerOcProgrammed')}
                          </th>
                          <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            {t('supply.productDetail.customerOcNonProgrammed')}
                          </th>
                          <th className="px-2.5 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            {t('supply.productDetail.customerTotalOc')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {demandByWeekRows.map((row) => (
                          <tr
                            key={row.week_start}
                            className={`hover:bg-gray-50 ${
                              row.is_past_week_programmed ? 'bg-red-50' : ''
                            }`}
                          >
                            <td className="px-2.5 py-1.5 text-xs text-gray-800 whitespace-nowrap">
                              <span className="font-semibold">{isoWeekLabel(row.week_start)}</span>
                              <span className="mx-1 text-gray-400">|</span>
                              <span className="text-xs text-gray-500">{row.week_start}</span>
                            </td>
                            <td
                              className={`px-2.5 py-1.5 text-xs text-right ${
                                row.is_past_week_programmed ? 'text-red-700 font-semibold' : 'text-gray-700'
                              }`}
                            >
                              {row.planned_qty.toFixed(2)}
                            </td>
                            <td className="px-2.5 py-1.5 text-xs text-right text-gray-700">{row.non_planned_qty.toFixed(2)}</td>
                            <td
                              className={`px-2.5 py-1.5 text-xs text-right font-semibold ${
                                row.is_past_week_programmed ? 'text-red-800' : 'text-gray-900'
                              }`}
                            >
                              {row.total_qty.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {customerOrdersRows.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    {(() => {
                      const showPurchaseAssignmentColumn = salesData?.is_on_demand === true;
                      const totalLabelColSpan = showPurchaseAssignmentColumn ? 4 : 3;

                      const renderPurchaseAssignmentIndicator = (
                        state: 'ASSOCIATED' | 'PENDING' | 'NOT_PROGRAMMED' | undefined
                      ) => {
                        if (state === 'ASSOCIATED') {
                          return (
                            <span
                              className="inline-flex h-4 w-4 items-center justify-center rounded border border-emerald-500 bg-emerald-100 text-[10px] font-bold text-emerald-700"
                              title={t('supply.productDetail.customerPurchaseAssignmentAssociated')}
                            >
                              ✓
                            </span>
                          );
                        }
                        if (state === 'PENDING') {
                          return (
                            <span
                              className="inline-flex h-4 w-4 items-center justify-center rounded border border-amber-500 bg-amber-100 text-[10px] font-bold text-amber-700"
                              title={t('supply.productDetail.customerPurchaseAssignmentPending')}
                            >
                              •
                            </span>
                          );
                        }
                        return (
                          <span
                            className="inline-flex h-4 w-4 items-center justify-center rounded border border-gray-300 bg-white"
                            title={t('supply.productDetail.customerPurchaseAssignmentNotProgrammed')}
                          />
                        );
                      };

                      return (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              {showPurchaseAssignmentColumn && (
                                <th
                                  scope="col"
                                  className="px-2.5 py-1.5 text-center text-[10px] font-semibold text-gray-600 uppercase tracking-wider"
                                >
                                  {t('supply.productDetail.customerPurchaseAssignmentColumn')}
                                </th>
                              )}
                              <th
                                scope="col"
                                className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider"
                              >
                                {t('supply.productDetail.customerOrderColumn')}
                              </th>
                              <th
                                scope="col"
                                className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider"
                              >
                                {t('supply.productDetail.customerClientColumn')}
                              </th>
                              <th
                                scope="col"
                                className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider"
                              >
                                Stato
                              </th>
                              <th
                                scope="col"
                                className="px-2.5 py-1.5 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider"
                              >
                                Qtà Comp.
                              </th>
                              <th
                                scope="col"
                                className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider"
                              >
                                Data ordine
                              </th>
                              <th
                                scope="col"
                                className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider"
                              >
                                Data consegna
                              </th>
                              <th
                                scope="col"
                                className="px-2.5 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider"
                              >
                                Data produzione
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {customerOrdersPageRows.map((row) => (
                              <tr
                                key={row.id}
                                className={`hover:bg-gray-50 ${
                                  row.is_component_row
                                    ? (row.planning_status === 'PROGRAMMATO' && row.parent_stock_insufficient)
                                      ? 'bg-red-50'
                                      : ''
                                    : row.is_past_programmed
                                      ? 'bg-red-50'
                                      : ''
                                }`}
                              >
                                {showPurchaseAssignmentColumn && (
                                  <td className="px-2.5 py-1.5 text-xs text-center whitespace-nowrap">
                                    {renderPurchaseAssignmentIndicator(row.purchase_assignment_state)}
                                  </td>
                                )}
                                <td className="px-2.5 py-1.5 text-xs font-medium text-gray-900 whitespace-nowrap">
                                  {row.sales_order_number}
                                </td>
                                <td className="px-2.5 py-1.5 text-xs text-gray-700 whitespace-nowrap">
                                  {row.customer || '—'}
                                </td>
                                <td className="px-2.5 py-1.5 text-xs whitespace-nowrap">
                                  <span
                                    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ${
                                      row.planning_status === 'PROGRAMMATO'
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : 'bg-amber-100 text-amber-800'
                                    }`}
                                  >
                                    {row.planning_status}
                                  </span>
                                </td>
                                <td className="px-2.5 py-1.5 text-xs text-gray-700 text-right whitespace-nowrap">
                                  {Number(row.qty_component ?? 0).toFixed(2)}
                                </td>
                                <td className="px-2.5 py-1.5 text-xs text-gray-700 whitespace-nowrap">
                                  {row.order_date ?? '—'}
                                </td>
                                <td
                                  className={`px-2.5 py-1.5 text-xs whitespace-nowrap ${
                                    row.is_past_programmed ? 'text-red-700 font-medium' : 'text-gray-700'
                                  }`}
                                >
                                  {row.delivery_date ? isoWeekLabel(row.delivery_date) : '—'}
                                </td>
                                <td className="px-2.5 py-1.5 text-xs text-gray-700 whitespace-nowrap">
                                  {row.production_date ? isoWeekLabel(row.production_date) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50 border-t border-gray-200">
                            <tr>
                              <td colSpan={totalLabelColSpan} className="px-2.5 py-1.5 text-[10px] font-semibold text-gray-700">
                                Totale
                              </td>
                              <td className="px-2.5 py-1.5 text-[10px] font-semibold text-right text-gray-900">
                                {ordersTotalQty.toFixed(2)}
                              </td>
                              <td colSpan={3} />
                            </tr>
                          </tfoot>
                        </table>
                      );
                    })()}
                    <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                      <span>
                        {t('supply.productDetail.paginationShowingOf', {
                          from: customerOrdersStartIndex + 1,
                          to: Math.min(customerOrdersStartIndex + customerOrdersPageRows.length, customerOrdersRows.length),
                          total: customerOrdersRows.length,
                        })}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCustomerOrdersPage((p) => Math.max(1, p - 1))}
                          disabled={safeCustomerOrdersPage <= 1}
                          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {t('supply.productDetail.paginationPrev')}
                        </button>
                        <span>
                          {safeCustomerOrdersPage}/{customerOrdersTotalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setCustomerOrdersPage((p) => Math.min(customerOrdersTotalPages, p + 1))
                          }
                          disabled={safeCustomerOrdersPage >= customerOrdersTotalPages}
                          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {t('supply.productDetail.paginationNext')}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-2">
                    No hay líneas de pedido cliente para mostrar con los datos actuales.
                  </p>
                )}
              </div>
            )}

          {/* Pedidos cliente / Ordini Clienti */}
          {salesData?.is_on_demand && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                {productionWeek
                  ? soExternalId
                    ? t('supply.productDetail.customerOrdersLinkedToThisWeekSo', { so: soExternalId })
                    : t('supply.productDetail.customerOrdersLinkedToThisWeek')
                  : t('supply.productDetail.customerOrdersLinkedToPurchaseTitle')}
              </h3>
              {salesData.bom_traces && salesData.bom_traces.length > 0 ? (
                <>
                  {(() => {
                    const today = new Date();
                    const dayOfWeek = today.getDay();
                    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                    const currentWeekMonday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diffToMonday);
                    const currentWeekStr = currentWeekMonday.toISOString().slice(0, 10);
                    const bomLeadTime = visibleProduct?.lead_time ?? (visibleProduct as any)?.inventory_policy__lead_time_days ?? 0;

                    const normalTraces = productionWeek
                      ? salesData.bom_traces
                      : salesData.bom_traces.filter(
                          (trace) => !(trace.week_bucket && trace.week_bucket < currentWeekStr)
                        );
                    const redTraces = productionWeek
                      ? []
                      : salesData.bom_traces.filter(
                          (trace) => trace.week_bucket && trace.week_bucket < currentWeekStr
                        );

                    return (
                      <>
                        {normalTraces.length > 0 && (
                          <>
                          <div className="overflow-x-auto rounded-lg border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th scope="col" className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    <div className="flex flex-col items-center gap-1">
                                      <span>{t('supply.productDetail.bomTraceManaged')}</span>
                                      <input
                                        type="checkbox"
                                        disabled={traceUpdating || normalTraces.length === 0}
                                        checked={normalTraces.length > 0 && normalTraces.every((r) => !!r.externally_managed)}
                                        onChange={async (e) => {
                                          const traceIds = normalTraces.map((r) => r.id).filter((id) => typeof id === 'number');
                                          await handleTraceExternalManagedUpdate(traceIds, e.target.checked);
                                        }}
                                      />
                                    </div>
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    {t('supply.productDetail.bomTraceOrderRef')}
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    {t('supply.productDetail.bomTraceLineNumber')}
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    {t('supply.productDetail.bomTraceQtyComponent')}
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    {t('supply.productDetail.bomTraceNeedWeek')}
                                  </th>
                                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    {t('supply.productDetail.bomTraceProductionWeek')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {normalTraces.map((trace, idx) => {
                                  const weekGroupKey = trace.production_week_bucket ?? trace.week_bucket ?? '';
                                  const prevWeekGroupKey =
                                    idx === 0
                                      ? ''
                                      : normalTraces[idx - 1].production_week_bucket ??
                                        normalTraces[idx - 1].week_bucket ??
                                        '';
                                  const isFirstInWeek = idx === 0 || weekGroupKey !== prevWeekGroupKey;
                                  const rowUrgentLt = isBomTraceRowUrgent(trace.week_bucket, bomLeadTime);
                                  return (
                                    <tr
                                      key={trace.id}
                                      className={`hover:bg-gray-50 ${
                                        rowUrgentLt
                                          ? 'bg-red-100'
                                          : isFirstInWeek
                                            ? 'border-t-2 border-gray-300 bg-gray-50/50'
                                            : 'bg-white'
                                      }`}
                                    >
                                      <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                                        <input
                                          type="checkbox"
                                          checked={!!trace.externally_managed}
                                          disabled={traceUpdating || typeof trace.id !== 'number'}
                                          onChange={(e) => {
                                            if (typeof trace.id !== 'number') return;
                                            handleTraceExternalManagedUpdate([trace.id], e.target.checked);
                                          }}
                                        />
                                      </td>
                                      <td className="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                                        <span>{trace.order_ref ?? '—'}</span>
                                        {trace.outside_lt ? (
                                          <span className="ml-1 inline-flex rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                            {t('supply.productDetail.outsideLtBadge')}
                                          </span>
                                        ) : null}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                                        {trace.line_number ?? '—'}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                                        {trace.qty_component}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                                        {trace.week_bucket ?? '—'}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                                        {trace.production_week_bucket ?? '—'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                <tr>
                                  <td colSpan={3} className="px-3 py-3 text-sm font-semibold text-gray-900">
                                    {t('supply.productDetail.bomTraceTotals')}
                                  </td>
                                  <td className="px-3 py-3 text-sm font-semibold text-gray-900 text-right">
                                    {normalTraces.reduce((s, t) => s + t.qty_component, 0)}
                                  </td>
                                  <td colSpan={2} className="px-3 py-3" />
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                          </>
                        )}

                        {redTraces.length > 0 && (
                          <div className="mt-4">
                            <p className="mb-2 text-sm font-semibold text-red-700">
                              {t('supply.productDetail.bomTraceOverdueTitle')}
                            </p>
                            <div className="overflow-x-auto rounded-lg border border-red-200">
                              <table className="min-w-full divide-y divide-red-200">
                                <thead className="bg-red-50">
                                  <tr>
                                    <th scope="col" className="px-3 py-2 text-center text-xs font-semibold text-red-700 uppercase tracking-wider">
                                      <div className="flex flex-col items-center gap-1">
                                        <span>{t('supply.productDetail.bomTraceManaged')}</span>
                                        <input
                                          type="checkbox"
                                          disabled={traceUpdating || redTraces.length === 0}
                                          checked={redTraces.length > 0 && redTraces.every((r) => !!r.externally_managed)}
                                          onChange={async (e) => {
                                            const traceIds = redTraces.map((r) => r.id).filter((id) => typeof id === 'number');
                                            await handleTraceExternalManagedUpdate(traceIds, e.target.checked);
                                          }}
                                        />
                                      </div>
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-red-700 uppercase tracking-wider">
                                      {t('supply.productDetail.bomTraceOrderRef')}
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-red-700 uppercase tracking-wider">
                                      {t('supply.productDetail.bomTraceLineNumber')}
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-semibold text-red-700 uppercase tracking-wider">
                                      {t('supply.productDetail.bomTraceQtyComponent')}
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-red-700 uppercase tracking-wider">
                                      {t('supply.productDetail.bomTraceNeedWeek')}
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-red-700 uppercase tracking-wider">
                                      {t('supply.productDetail.bomTraceProductionWeek')}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-red-100">
                                  {redTraces.map((trace) => {
                                    const rowUrgentLt = isBomTraceRowUrgent(trace.week_bucket, bomLeadTime);
                                    return (
                                    <tr
                                      key={trace.id}
                                      className={
                                        rowUrgentLt
                                          ? 'bg-red-200/90 hover:bg-red-200'
                                          : 'bg-red-50/60 hover:bg-red-50'
                                      }
                                    >
                                      <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                                        <input
                                          type="checkbox"
                                          checked={!!trace.externally_managed}
                                          disabled={traceUpdating || typeof trace.id !== 'number'}
                                          onChange={(e) => {
                                            if (typeof trace.id !== 'number') return;
                                            handleTraceExternalManagedUpdate([trace.id], e.target.checked);
                                          }}
                                        />
                                      </td>
                                      <td className="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                                        <span>{trace.order_ref ?? '—'}</span>
                                        {trace.outside_lt ? (
                                          <span className="ml-1 inline-flex rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                            {t('supply.productDetail.outsideLtBadge')}
                                          </span>
                                        ) : null}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                                        {trace.line_number ?? '—'}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                                        {trace.qty_component}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-red-600 font-medium whitespace-nowrap">
                                        {trace.week_bucket ?? '—'}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                                        {trace.production_week_bucket ?? '—'}
                                      </td>
                                    </tr>
                                  );
                                  })}
                                </tbody>
                                <tfoot className="bg-red-100 border-t-2 border-red-300">
                                  <tr>
                                    <td colSpan={3} className="px-3 py-3 text-sm font-semibold text-red-900">
                                      {t('supply.productDetail.bomTraceTotals')}
                                    </td>
                                    <td className="px-3 py-3 text-sm font-semibold text-red-900 text-right">
                                      {redTraces.reduce((s, t) => s + t.qty_component, 0)}
                                    </td>
                                    <td colSpan={2} className="px-3 py-3" />
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <p className="text-sm text-gray-500 py-4">
                  {t('supply.productDetail.bomTraceNoData')}
                </p>
              )}
            </div>
          )}

          {!loading &&
            salesData?.bom_explosion &&
            visibleProduct &&
            salesData.product.id === visibleProduct.id &&
            salesData.product.product_type !== 'FINISHED_DIST' && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  {t('supply.productDetail.bomExplosionTitle')}
                </h3>
                <BomExplosionPayloadView
                  payload={salesData.bom_explosion}
                  t={t}
                  selectedProductId={visibleProduct.id}
                  onSelectProduct={handleBomExplosionProductSelect}
                />
              </div>
            )}

        </div>
      </div>
      <ReceivePurchaseOrderDialog
        open={receiveDraft != null}
        title={t('supply.receivePo.title')}
        busy={markingReceivedId != null}
        error={receiveError}
        showQuantity
        orderedQty={receiveDraft?.orderedQty}
        alreadyReceived={receiveDraft?.alreadyReceived}
        pendingQty={receiveDraft?.pendingQty}
        onClose={() => {
          if (markingReceivedId != null) return;
          setReceiveDraft(null);
          setReceiveError(null);
        }}
        onConfirm={(payload) => {
          void handleMarkInTransitReceived(payload);
        }}
      />
    </>
  );

  // Renderizar usando Portal directamente en el body para evitar limitaciones de contenedores padre
  return createPortal(sidebarContent, document.body);
}
